import React, {FC, useEffect, useState} from "react"

import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import OnboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import authentication from "@irati/auth"
import GenericModal from "@irati/ui/src/GenericModal"
import {H1, Body} from "@irati/ui/src/Typography"
import PropTypes from "prop-types"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {adminDashboardTracking} from "@irati-tracking/admin"
import RewardsImage from "@irati/ui/images/globalRewards.png"
import Flex from "@irati/ui/src/Flex"
import Link, {ButtonRouterLink} from "@irati/ui/src/Link"
import mainTheme from "@irati/ui/src/global/theme"
import {WalletRoutes} from "@irati-web/wallet/src/enums/Routes"
interface Context {
  apps: {[key: string]: string}
}
interface Props extends InjectIntl {
  showModal?: boolean
  onClose?: () => void
}

const GlobalRewardWalletModal: FC<Props> = ({showModal, onClose, formatMessage}, {apps}: Context) => {
  const [renderModal, setRenderModal] = useState(false)
  const [canBeShown, setCanBeShown] = useState(false)
  const {WALLET} = apps
  const customVisibilityHandler = typeof showModal === "boolean"
  useEffect(() => {
    const canRewardEmployees = !!authentication.apps("wallet.flexiRewards.canRewardEmployees")
    if (!canRewardEmployees) {
      return
    }
    OnboardingService.get(authentication.user().uuid!).then(({userProgress}) => {
      const rewardsPopupStepNotCompleted = !userProgress!.find(({step}) => step === OnboardingStep.HAS_SEEN_NEW_REWARDS_POPUP)
      setCanBeShown(rewardsPopupStepNotCompleted)
    })
  }, [])
  useEffect(() => {
    if (!canBeShown) {
      return
    }
    if (!customVisibilityHandler) {
      onOpen()
      return
    }
    if (!showModal) {
      return
    }
    onOpen()
  }, [canBeShown, showModal])
  const onOpen = () => {
    OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_SEEN_NEW_REWARDS_POPUP)
    setRenderModal(true)
  }

  const handleClose = () => {
    adminDashboardTracking.clickOutRewardWalletRewardsModal()
    setRenderModal(false)
    onClose?.()
  }

  const onGoToWalletClick = () => {
    adminDashboardTracking.clickRewardWalletRewardsModal()
    setRenderModal(false)
  }

  if (!canBeShown) {
    return null
  }

  return (
    <GenericModal customWidth={1150} show={renderModal} hideActions onHide={handleClose}>
      <Flex alignItems="center" justifyContent="space-between" gap={`0 ${mainTheme.spacing.four}`}>
        <div>
          <H1 marginBottom={mainTheme.spacing.three}>
            <FormattedMessage id="platform.adminNavigation.rewards.title" />
          </H1>
          <Flex alignItems="center" marginBottom={mainTheme.spacing.two}>
            <Icon icon={IconTypes.globe} marginRight size={30} />
            <Body>
              <FormattedMessage id="platform.adminNavigation.rewards.body1" />
            </Body>
          </Flex>
          <Flex alignItems="center" marginBottom={mainTheme.spacing.four}>
            <Icon icon={IconTypes.shoppingCart} marginRight size={30} />
            <Body>
              <FormattedMessage id="platform.adminNavigation.rewards.body2" />
            </Body>
          </Flex>
          <ButtonRouterLink app={WALLET} to={WalletRoutes.FLEXI_REWARD} primary onClick={onGoToWalletClick} marginRight>
            <FormattedMessage id="platform.adminNavigation.rewards.button1" />
          </ButtonRouterLink>
          <Flex inline alignItems="center">
            <Link
              onClick={adminDashboardTracking.clickRewardsVideoRewardsModal}
              href={formatMessage({id: "platform.adminNavigation.rewards.button2.link"})}
              target="_blank">
              <FormattedMessage id="platform.adminNavigation.rewards.button2.text" />
            </Link>
            <Icon icon={IconTypes.export} marginLeft />
          </Flex>
        </div>
        <img src={RewardsImage} alt="Global rewards popup" />
      </Flex>
    </GenericModal>
  )
}
GlobalRewardWalletModal.contextTypes = {
  apps: PropTypes.object.isRequired
}
export default injectIntl(GlobalRewardWalletModal)
